<template>
  <div class="mt-4" v-if="item">
    <div class="mx-0" v-if="is_event_revenue">
      <h3>{{ $t("screener.monetization.revenue.about_event.title") }}</h3>
      <v-divider class="my-2"></v-divider>
      <v-layout row wrap v-if="is_event_revenue">
        <v-flex xs6 sm6 md4>
          <!-- Screening -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("event.title") }}
            </v-flex>
            <v-flex xs6>
              {{ item.screener }}
            </v-flex>
          </v-layout>
          <!-- Flims -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("films.title") }}
            </v-flex>
            <v-flex xs6>
              <v-chip
                color="secondary"
                outline
                small
                v-for="film in item.films"
                :key="film.id"
                >{{ film.title }}</v-chip
              >
            </v-flex>
          </v-layout>

          <!-- Right Holders -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("company.dashboard.members.section.right_holders") }}
            </v-flex>
            <v-flex xs6>
              <template v-if="item.right_holders.length">
                <v-chip
                  color="secondary"
                  outline
                  v-for="right_holder in item.right_holders"
                  :key="right_holder"
                  small
                  >{{ right_holder }}</v-chip
                >
              </template>
              <template v-else>---</template>
            </v-flex>
          </v-layout>
          <!-- Amount Status -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">{{
              $t("screener.monetization.revenue.about_event.amount_status")
            }}</v-flex>
            <v-flex xs6>
              <v-chip
                v-if="item.is_revenue_processed"
                color="success"
                small
                dark
                >{{ $t("screener.status.paid") }}</v-chip
              >
              <v-chip v-else color="red" small dark>
                {{ $t("screener.status.unpaid") }}
              </v-chip>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6 sm6 md4 offset-md2 offset-xs0>
          <!-- Currency -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">{{
              $t("screener.table.header.currency")
            }}</v-flex>
            <v-flex xs6>{{ item.currency }}</v-flex>
          </v-layout>
          <!-- Right Holder Share -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.total_right_holder_share") }}</v-flex
            >
            <v-flex xs6>{{ item.total_right_holder_share }} </v-flex>
          </v-layout>

          <!-- Total Company Share -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.total_company_share") }}
            </v-flex>
            <v-flex xs6
              >{{ getCurrencySymbol(item.currency) }}&nbsp;{{
                item.total_company_share
              }}
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Total revenue share the company earned
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <!-- Total Amount -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.total_amount") }}
            </v-flex>
            <v-flex xs6
              >{{ getCurrencySymbol(item.currency) }}&nbsp;{{
                item.total_amount
              }}
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Total amount after excluding payment gateway fee
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-divider class="my-2"></v-divider>
    </div>
    <div v-else>
      <h3>{{ $t("screener.monetization.revenue.about_film.title") }}</h3>
      <v-divider class="my-2"></v-divider>
      <v-layout row wrap>
        <v-flex xs6 sm6 md4>
          <!-- Film -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.film") }}
            </v-flex>
            <v-flex xs6>
              {{ item.film }}
            </v-flex>
          </v-layout>
          <!-- Flims -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.screenings") }}
            </v-flex>
            <v-flex xs6>
              <v-chip
                color="secondary"
                outline
                small
                v-for="screener in item.screeners"
                :key="screener.id"
                >{{ screener.title }}</v-chip
              >
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Screenings in which the film is screened
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>

          <!-- Right Holder -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("film.rightholder.title") }}
            </v-flex>
            <v-flex xs6>
              <v-flex xs6>
                <span v-if="item.right_holder">{{ item.right_holder }}</span>
                <span v-else>---</span>
                <v-tooltip top color="white">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small class="px-2"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <div class="grey--text text--darken-3 body-1">
                    Film's Right Holder
                  </div>
                </v-tooltip>
              </v-flex>
            </v-flex>
          </v-layout>
          <!-- Right Holder share percentage -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.right_holder_share_percent") }}
            </v-flex>
            <v-flex xs6>
              <v-flex xs6>
                <span v-if="item.right_holder_share"
                  >{{ item.right_holder_share }} %
                </span>
                <span v-else>---</span>
                <v-tooltip top color="white">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small class="px-2"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <div class="grey--text text--darken-3 body-1">
                    Right Holder Share Percentage
                  </div>
                </v-tooltip>
              </v-flex>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.monetization.minimum_guarantee") }}
            </v-flex>
            <v-flex xs6>
              <span v-if="item.minimum_guarantee">{{
                item.minimum_guarantee
              }}</span>
              <span v-else>---</span>
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  {{ $t("screener.film.minimum_guarantee") }}
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <!-- Amount Status -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">{{
              $t("screener.monetization.revenue.about_event.amount_status")
            }}</v-flex>
            <v-flex xs6>
              <v-chip
                v-if="item.is_revenue_processed"
                color="success"
                small
                dark
                >{{ $t("screener.status.paid") }}</v-chip
              >
              <v-chip v-else color="red" small dark>
                {{ $t("screener.status.unpaid") }}
              </v-chip>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6 sm6 md4 offset-md2 offset-xs0>
          <!-- Currency -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">{{
              $t("screener.table.header.currency")
            }}</v-flex>
            <v-flex xs6
              >{{ item.currency }}
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Currency used in the company
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <!-- Fee -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">{{
              $t("screener.monetization.fees")
            }}</v-flex>
            <v-flex xs6>
              <span v-if="item.fees">{{ item.fees }}</span>
              <span v-else>---</span>
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  {{ $t("screener.film.fees") }}
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <!-- Right Holder Share -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.total_right_holder_share") }}</v-flex
            >
            <v-flex xs6
              >{{ item.total_right_holder_share }}
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Total revenue share the right holder earned
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <!-- Right Holder Earned Revenue -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.right_holder_earned_revenue") }}
            </v-flex>
            <v-flex xs6
              >{{ getCurrencySymbol(item.currency) }}&nbsp;{{
                item.revenue_earned
              }}
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Revenue, right holder earned after excluding minimum guarantee
                  & fees
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <!-- Total Company Share -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.total_company_share") }}
            </v-flex>
            <v-flex xs6
              >{{ getCurrencySymbol(item.currency) }}&nbsp;{{
                item.total_company_share
              }}
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Total revenue share the company earned
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
          <!-- Total Amount -->
          <v-layout row wrap class="py-1">
            <v-flex xs6 class="grey--text text--darken-1">
              {{ $t("screener.table.header.total_amount") }}
            </v-flex>
            <v-flex xs6
              >{{ getCurrencySymbol(item.currency) }}&nbsp;{{
                item.total_amount
              }}
              <v-tooltip top color="white">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small class="px-2"
                    >mdi-information-outline</v-icon
                  >
                </template>
                <div class="grey--text text--darken-3 body-1">
                  Total revenue earned on this film
                </div>
              </v-tooltip>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-divider class="my-2"></v-divider>
    </div>
  </div>
</template>

<script>
import { getCurrencySymbol } from "@/helper";

export default {
  name: "AboutRevenue",
  props: {
    item: {
      type: Object,
      default: null,
    },
    is_event_revenue: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    getCurrencySymbol(currency) {
      return getCurrencySymbol(currency);
    },
  },
};
</script>
